import { submitBugReport } from "../bug/bug";

let bugReportModalEl: HTMLElement | null = null;
let bugMessageTextareaEl: HTMLTextAreaElement | null = null;
let bugSubmitButtonEl: HTMLButtonElement | null = null;
let bugCancelButtonEl: HTMLButtonElement | null = null;
let bugStatusEl: HTMLElement | null = null;

let isSubmitting = false;

export function initializeBugReportElements(): void {
  bugReportModalEl = document.getElementById("bugReportModal");
  bugMessageTextareaEl = document.getElementById(
    "bugMessageTextarea",
  ) as HTMLTextAreaElement;
  bugSubmitButtonEl = document.getElementById(
    "bugSubmitButton",
  ) as HTMLButtonElement;
  bugCancelButtonEl = document.getElementById(
    "bugCancelButton",
  ) as HTMLButtonElement;
  bugStatusEl = document.getElementById("bugStatus");

  if (bugSubmitButtonEl) {
    bugSubmitButtonEl.addEventListener("click", handleSubmitBugReport);
  }

  if (bugCancelButtonEl) {
    bugCancelButtonEl.addEventListener("click", hideBugReportModal);
  }

  if (bugMessageTextareaEl) {
    bugMessageTextareaEl.addEventListener("input", handleTextareaInput);
    bugMessageTextareaEl.addEventListener("keydown", handleTextareaKeydown);
  }
}

export function showBugReportModal(): void {
  if (!bugReportModalEl) {
    console.error("[BUG UI] Bug report modal not initialized");
    return;
  }

  bugReportModalEl.classList.add("show");

  if (bugMessageTextareaEl) {
    bugMessageTextareaEl.value = "";
    bugMessageTextareaEl.focus();
  }

  if (bugStatusEl) {
    bugStatusEl.textContent = "";
    bugStatusEl.className = "";
  }

  updateSubmitButtonState();
}

export function hideBugReportModal(): void {
  if (!bugReportModalEl) return;

  bugReportModalEl.classList.remove("show");
  isSubmitting = false;
  updateSubmitButtonState();
}

function handleTextareaInput(): void {
  updateSubmitButtonState();
}

function handleTextareaKeydown(event: KeyboardEvent): void {
  if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
    event.preventDefault();
    handleSubmitBugReport();
  }

  if (event.key === "Escape") {
    event.preventDefault();
    hideBugReportModal();
  }
}

function updateSubmitButtonState(): void {
  if (!bugSubmitButtonEl || !bugMessageTextareaEl) return;

  const hasContent = bugMessageTextareaEl.value.trim().length > 0;
  const canSubmit = hasContent && !isSubmitting;

  bugSubmitButtonEl.disabled = !canSubmit;
  bugSubmitButtonEl.textContent = isSubmitting
    ? "SUBMITTING..."
    : "SUBMIT BUG REPORT";
}

async function handleSubmitBugReport(): Promise<void> {
  if (!bugMessageTextareaEl || !bugStatusEl || isSubmitting) return;

  const message = bugMessageTextareaEl.value.trim();
  if (!message) {
    showStatus("Please enter a bug description", "error");
    return;
  }

  isSubmitting = true;
  updateSubmitButtonState();
  showStatus("Submitting bug report...", "info");

  try {
    const result = await submitBugReport(message);

    if (result.success) {
      showStatus("Bug reported. Thank you!", "success");

      setTimeout(() => {
        hideBugReportModal();
      }, 2000);
    } else {
      showStatus(`Failed to submit bug report: ${result.error}`, "error");
    }
  } catch (error) {
    console.error("[BUG UI] Error submitting bug report:", error);
    showStatus("Failed to submit bug report. Please try again.", "error");
  } finally {
    isSubmitting = false;
    updateSubmitButtonState();
  }
}

function showStatus(message: string, type: "success" | "error" | "info"): void {
  if (!bugStatusEl) return;

  bugStatusEl.textContent = message;
  bugStatusEl.className = `bug-status ${type}`;
}

export function isBugReportModalVisible(): boolean {
  return bugReportModalEl?.classList.contains("show") || false;
}

export function createBugReportModal(): void {
  if (document.getElementById("bugReportModal")) {
    return;
  }

  const modalHTML = `
    <div id="bugReportModal" class="bug-report-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>REPORT BUG</h2>
          <button id="bugCancelButton" class="close-button">×</button>
        </div>
        <div class="modal-body">
          <div class="bug-form">
            <textarea
              id="bugMessageTextarea"
              placeholder="Describe what went wrong..."
              rows="4"
              maxlength="500"
            ></textarea>
            <div class="form-footer">
              <div id="bugStatus" class="bug-status"></div>
              <div class="button-group">
                <button id="bugCancelButton" class="button secondary">CANCEL</button>
                <button id="bugSubmitButton" class="button primary" disabled>SUBMIT</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML("beforeend", modalHTML);
}

export function initializeBugReportUI(): void {
  createBugReportModal();
  initializeBugReportElements();
}

export function getBugReportUIStats(): {
  modalVisible: boolean;
  elementsInitialized: boolean;
  isSubmitting: boolean;
} {
  return {
    modalVisible: isBugReportModalVisible(),
    elementsInitialized: !!(
      bugReportModalEl &&
      bugMessageTextareaEl &&
      bugSubmitButtonEl
    ),
    isSubmitting,
  };
}
